import { useState } from "react";
import { ScanSearch, ArrowRight, Loader2 } from "lucide-react";
import { ClearableInput } from "@/components/shared/ClearableInput";
import { Button } from "@/components/ui/button";
import { isValidAsin } from "@/utils/asin";

interface SPIEntryFormProps {
  onSubmit: (asin: string) => void;
  isSubmitting?: boolean;
}

export function SPIEntryForm({ onSubmit, isSubmitting = false }: SPIEntryFormProps) {
  const [asin, setAsin] = useState("");
  const [error, setError] = useState("");

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (isSubmitting) return;
    const trimmed = asin.trim();
    if (!trimmed) return;
    if (!isValidAsin(trimmed)) {
      setError("Invalid ASIN format. ASINs must be exactly 10 alphanumeric characters.");
      return;
    }
    setError("");
    onSubmit(trimmed.toUpperCase());
  };

  const trySamples = ["B073JYC4XM", "B00FLYWNYQ"];

  return (
    <div className="flex flex-col items-center justify-center min-h-[60vh]">
      <div className="w-full max-w-lg bg-card border border-border rounded-2xl p-8 text-center">
        <div className="mx-auto mb-4 h-14 w-14 rounded-full bg-primary/10 flex items-center justify-center">
          <ScanSearch className="h-7 w-7 text-primary" />
        </div>

        <h2 className="text-xl font-bold text-foreground mb-2">
          Add an ASIN
        </h2>
        <p className="text-muted-foreground text-sm mb-6">
          Audit your listing against the 15 AI-driven questions Amazon COSMO
          uses to determine search rankings.
        </p>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-xs font-semibold text-muted-foreground mb-1.5 text-left tracking-wide uppercase">
              Amazon ASIN
            </label>
            <ClearableInput
              value={asin}
              onChange={(value) => {
                setAsin(value);
                if (error) setError("");
              }}
              placeholder="e.g. B073JYC4XM"
              className={error ? "border-destructive" : ""}
            />
            {error && (
              <p className="text-xs text-destructive mt-1 text-left">{error}</p>
            )}
          </div>

          <Button
            type="submit"
            className="w-full h-11 text-base gap-2"
            disabled={!asin.trim() || isSubmitting}
          >
            {isSubmitting && <Loader2 className="h-4 w-4 animate-spin" />}
            Start Audit
            {!isSubmitting && <ArrowRight className="h-4 w-4" />}
          </Button>
        </form>
      </div>

      <p className="text-xs text-muted-foreground mt-4">
        Try:{" "}
        {trySamples.map((sample, i) => (
          <span key={sample}>
            {i > 0 && "  "}
            <button
              onClick={() => {
                if (isSubmitting) return;
                setAsin(sample);
                onSubmit(sample);
              }}
              disabled={isSubmitting}
              className="text-primary hover:underline font-mono disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {sample}
            </button>
          </span>
        ))}
      </p>
    </div>
  );
}
